import { useState, useEffect } from "react";
import QuoteForm from "@/pages/QuoteForm";
import {
  Phone,
  Mail,
  Menu,
  X,
  Siren,
  FlaskConical,
  Pill,
  CalendarClock,
  FileText,
  Stethoscope,
  Timer,
  ShieldCheck,
  UserCheck,
  Radio,
  Clock,
  MapPin,
  ArrowRight,
} from "lucide-react";

const LOGO =
  "https://static.prod-images.emergentagent.com/jobs/e7b7daa6-8ea2-43e6-aa83-dd1953fd740f/images/e9083665197bd9fc8ecd9310b436467efeba6574e5d4ac4a775da412c29c3983.png";

const IMG = {
  hero: "https://images.unsplash.com/photo-1554734867-bf3c00a49371?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxhbWJ1bGFuY2UlMjBkcml2aW5nJTIwbW90aW9uJTIwYmx1cnxlbnwwfHx8fDE3ODMxMDc2MzZ8MA&ixlib=rb-4.1.0&q=85",
  lab: "https://images.pexels.com/photos/8533080/pexels-photo-8533080.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  pro: "https://images.unsplash.com/photo-1690306816872-91063f6de36b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwcHJvZmVzc2lvbmFsJTIwZGVsaXZlcnl8ZW58MHx8fHwxNzgzMTA3NjE4fDA&ixlib=rb-4.1.0&q=85",
  map: "https://images.unsplash.com/photo-1556033681-83abea291a96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxuYXNodmlsbGUlMjBza3lsaW5lfGVufDB8fHx8MTc4MzEwNzYxOHww&ixlib=rb-4.1.0&q=85",
};

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Coverage", href: "#coverage" },
  { label: "Contact", href: "#quote" },
];

const SERVICES = [
  { icon: Siren, title: "STAT / Emergency Deliveries", desc: "Immediate, priority-dispatch runs for time-critical specimens, blood and supplies.", accent: true },
  { icon: FlaskConical, title: "Laboratory Specimens", desc: "Temperature-aware, chain-of-custody handling of lab samples and diagnostics." },
  { icon: Pill, title: "Pharmacy Deliveries", desc: "Secure medication and prescription transport to facilities and patients." },
  { icon: CalendarClock, title: "Scheduled Routes", desc: "Reliable daily and recurring courier routes built around your operations." },
  { icon: FileText, title: "Medical Records", desc: "Confidential, HIPAA-conscious transport of sensitive documents." },
  { icon: Stethoscope, title: "Medical Equipment", desc: "Careful transport of instruments, devices and healthcare equipment." },
  { icon: Timer, title: "Time-Critical Logistics", desc: "End-to-end healthcare logistics engineered for speed and accountability." },
];

const WHY = [
  { icon: ShieldCheck, title: "HIPAA-Conscious Handling", desc: "Trained protocols that respect patient privacy at every step." },
  { icon: UserCheck, title: "Professional Drivers", desc: "Vetted, courteous couriers who represent your facility well." },
  { icon: Radio, title: "Real-Time Communication", desc: "Live status updates and direct dispatcher access — never guessing." },
  { icon: Clock, title: "On-Time, Every Time", desc: "Dependable delivery windows your team can plan around." },
];

const AREAS = ["Columbia", "Spring Hill", "Franklin", "Murfreesboro", "Nashville", "Surrounding Middle TN"];

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const els = document.querySelectorAll("[data-animate]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("bsc-fade-up");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#111827]">
      {/* Header */}
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 border-b border-gray-200 bg-white"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-10">
          <a href="#top" data-testid="header-logo" className="flex items-center">
            <img src={LOGO} alt="BSC Rapid Med" className="h-16 w-auto md:h-20" />
          </a>
          <nav className="hidden items-center gap-9 md:flex">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
                className="text-sm font-medium text-[#0B2447]/70 transition-colors hover:text-[#0B2447]"
              >
                {n.label}
              </a>
            ))}
            <a
              href="tel:9313259599"
              data-testid="header-call"
              className="flex items-center gap-2 text-sm font-semibold text-[#0B2447]"
            >
              <Phone className="h-4 w-4 text-[#D90429]" strokeWidth={2} />
              931-325-9599
            </a>
            <a
              href="#quote"
              data-testid="header-cta"
              className="rounded-none bg-[#D90429] px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-[#b00321]"
            >
              Request Pickup
            </a>
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            className="text-[#0B2447] md:hidden"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div data-testid="mobile-menu" className="border-t border-gray-200 bg-white px-5 pb-5 md:hidden">
            <nav className="flex flex-col gap-1 pt-2">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setMenuOpen(false)}
                  className="py-3 text-sm font-medium text-[#0B2447]"
                >
                  {n.label}
                </a>
              ))}
              <a
                href="#quote"
                onClick={() => setMenuOpen(false)}
                className="mt-2 rounded-none bg-[#D90429] px-5 py-3 text-center text-sm font-semibold uppercase text-white"
              >
                Request Pickup
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section id="top" data-testid="hero" className="relative overflow-hidden bg-black">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36">
          <div className="max-w-3xl">
            <p className="mb-5 text-xs font-bold uppercase tracking-[0.25em] text-[#D90429]">
              Where Legacy Meets Logistics
            </p>
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Fast. Secure. Reliable Medical Courier Services.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-300">
              Serving Columbia, Spring Hill, Franklin, Murfreesboro, Nashville &
              all of Middle Tennessee with time-critical healthcare logistics.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href="#quote"
                data-testid="hero-cta-quote"
                className="group flex items-center justify-center gap-2 rounded-none bg-[#D90429] px-8 py-4 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-[#b00321]"
              >
                Request a Quote
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </a>
              <a
                href="tel:9313259599"
                data-testid="hero-cta-call"
                className="flex items-center justify-center gap-2 rounded-none border border-white/30 bg-white/5 px-8 py-4 text-sm font-semibold uppercase tracking-wide text-white backdrop-blur transition-colors hover:bg-white hover:text-black"
              >
                <Phone className="h-4 w-4" strokeWidth={2} /> Call Now
              </a>
            </div>
          </div>
        </div>
        {/* trust strip */}
        <div className="relative border-t border-gray-800 bg-black/60">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px px-5 md:grid-cols-4 md:px-10">
            {[
              ["Rapid", "Response Times"],
              ["HIPAA", "Conscious Handling"],
              ["Middle TN", "Local Coverage"],
              ["STAT", "Priority Runs"],
            ].map(([a, b]) => (
              <div key={b} className="py-6 text-center md:text-left">
                <div className="font-heading text-2xl font-extrabold text-white">{a}</div>
                <div className="text-xs uppercase tracking-wider text-gray-400">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" data-testid="services" className="bg-[#F8F9FA] py-24 px-5 md:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl" data-animate>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">
              What We Move
            </p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-[#0B2447] sm:text-4xl">
              Comprehensive Medical Courier Services
            </h2>
            <p className="mt-4 text-[#4B5563]">
              From emergency STAT runs to recurring routes, BSC Rapid Med handles
              your most sensitive deliveries with speed and accountability.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`service-card-${i}`}
                  data-animate
                  style={{ animationDelay: `${i * 60}ms` }}
                  className={`group border bg-white p-8 transition-colors ${
                    s.accent
                      ? "border-[#D90429]/40 hover:border-[#D90429]"
                      : "border-gray-200 hover:border-[#0B2447]"
                  }`}
                >
                  <div
                    className={`mb-6 flex h-12 w-12 items-center justify-center ${
                      s.accent ? "bg-[#D90429]" : "bg-[#0B2447]"
                    }`}
                  >
                    <Icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-[#0B2447]">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4B5563]">
                    {s.desc}
                  </p>
                </div>
              );
            })}
            <div
              data-animate
              className="flex flex-col justify-between bg-[#0B2447] p-8 text-white"
            >
              <div>
                <h3 className="font-heading text-lg font-bold">Need something else moved?</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-300">
                  Tell us what you need transported — we build custom logistics
                  solutions for healthcare partners.
                </p>
              </div>
              <a
                href="#quote"
                data-testid="services-cta"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#D90429]"
              >
                Get a Quote <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section id="why" data-testid="why" className="py-24 px-5 md:px-10">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 lg:grid-cols-2 lg:items-center">
          <div data-animate>
            <img
              src={IMG.pro}
              alt="Professional medical courier"
              className="h-full max-h-[520px] w-full object-cover"
            />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500" data-animate>
              Why Choose BSC Rapid Med
            </p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-[#0B2447] sm:text-4xl" data-animate>
              Built for trust. Driven for speed.
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2">
              {WHY.map((w, i) => {
                const Icon = w.icon;
                return (
                  <div key={w.title} data-testid={`why-item-${i}`} data-animate style={{ animationDelay: `${i * 70}ms` }}>
                    <div className="mb-4 flex h-11 w-11 items-center justify-center border border-[#0B2447]/20 bg-[#0B2447]/5">
                      <Icon className="h-5 w-5 text-[#0B2447]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-base font-bold text-[#0B2447]">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#4B5563]">{w.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section id="coverage" data-testid="coverage" className="relative overflow-hidden bg-[#0B2447]">
        <div className="absolute inset-0">
          <img src={IMG.map} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B2447] via-[#0B2447]/85 to-[#0B2447]/70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-24 md:px-10">
          <div className="max-w-2xl" data-animate>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#ff5470]">
              <MapPin className="h-4 w-4" strokeWidth={2} /> Coverage Area
            </p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Serving all of Middle Tennessee
            </h2>
            <p className="mt-4 text-gray-300">
              Local routes, fast response, and reliable critical logistics across
              the region — with on-time performance your team can count on.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {AREAS.map((a, i) => (
              <div
                key={a}
                data-testid={`coverage-area-${i}`}
                data-animate
                style={{ animationDelay: `${i * 50}ms` }}
                className="border border-white/15 bg-white/5 p-5 text-center backdrop-blur transition-colors hover:border-[#D90429] hover:bg-white/10"
              >
                <MapPin className="mx-auto mb-2 h-5 w-5 text-[#D90429]" strokeWidth={1.5} />
                <span className="text-sm font-semibold text-white">{a}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-gray-400" data-animate>
            Don't see your city? We likely still cover it — just ask.
          </p>
        </div>
      </section>

      {/* Quote / Contact */}
      <section id="quote" data-testid="quote" className="bg-[#F8F9FA] py-24 px-5 md:px-10">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 lg:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">
              Request a Quote
            </p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-[#0B2447] sm:text-4xl">
              Need a dependable medical courier partner?
            </h2>
            <p className="mt-4 text-[#4B5563]">
              Contact BSC Rapid Med today for fast, secure delivery solutions.
              Fill out the form and a dispatcher will get right back to you.
            </p>
            <div className="mt-10 space-y-5">
              <a href="tel:9313259599" data-testid="contact-phone" className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center bg-[#D90429]">
                  <Phone className="h-5 w-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">Call / Text</div>
                  <div className="font-heading text-lg font-bold text-[#0B2447]">931-325-9599</div>
                </div>
              </a>
              <a href="mailto:Roderick@bscrapidmed.com" data-testid="contact-email" className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center bg-[#0B2447]">
                  <Mail className="h-5 w-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">Email</div>
                  <div className="font-heading text-lg font-bold text-[#0B2447]">Roderick@bscrapidmed.com</div>
                </div>
              </a>
            </div>
          </div>
          <div>
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="site-footer" className="bg-[#0B2447] px-5 py-16 text-white md:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-10 border-b border-white/10 pb-10 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
                BSC RAPID MED
              </h2>
              <p className="mt-2 text-sm uppercase tracking-[0.25em] text-[#ff5470]">
                Where Legacy Meets Logistics
              </p>
            </div>
            <a
              href="#quote"
              data-testid="footer-cta"
              className="inline-flex items-center justify-center gap-2 rounded-none bg-[#D90429] px-8 py-4 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-[#b00321]"
            >
              Request a Quote <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </a>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-8 text-sm sm:grid-cols-3">
            <div>
              <div className="mb-2 text-xs uppercase tracking-wider text-gray-400">Phone</div>
              <a href="tel:9313259599" className="font-medium hover:text-[#ff5470]">931-325-9599</a>
            </div>
            <div>
              <div className="mb-2 text-xs uppercase tracking-wider text-gray-400">Email</div>
              <a href="mailto:Roderick@bscrapidmed.com" className="font-medium hover:text-[#ff5470]">Roderick@bscrapidmed.com</a>
            </div>
            <div>
              <div className="mb-2 text-xs uppercase tracking-wider text-gray-400">Service Area</div>
              <p className="font-medium">Middle Tennessee</p>
            </div>
          </div>
          <div className="mt-10 flex flex-col gap-2 text-xs text-gray-400 sm:flex-row sm:items-center sm:justify-between">
            <p>HIPAA-conscious handling · Professional, insured couriers</p>
            <p>© {new Date().getFullYear()} BSC Rapid Med. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
